import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';
import { useState } from 'react';
import { useMobileAppPromotion } from '../hooks/useMobileAppPromotion';
import { isAppBarSnoozed, snoozeAppBar } from '../lib/mobileApp';

/**
 * PD-16558 — a slim bar under the storefront header on phones, offering the
 * storefront's app in this phone's store. Closing it hides it for 14 days;
 * the menu's "Get the app" row stays for anyone who changes their mind.
 *
 * Shown only on a phone whose store has the app, never inside the app
 * itself, and never at tablet or desktop widths, where the header already
 * has room for the menu link.
 */
export function GetAppBar(): JSX.Element | null {
	const { t } = useTranslation();
	const promotion = useMobileAppPromotion();
	const [snoozed, setSnoozed] = useState(isAppBarSnoozed);

	if (promotion?.phoneStoreUrl == null || snoozed) {
		return null;
	}

	const { app, brandName, phoneStoreUrl } = promotion;

	return (
		<div
			role="region"
			aria-label={t('Get the app')}
			className="md:hidden flex items-center gap-3 px-3 py-2 bg-brand-surface border-b border-brand-border"
		>
			{app.appIconUrl != null && (
				<img
					src={app.appIconUrl}
					alt=""
					className="h-9 w-9 flex-none rounded-lg"
				/>
			)}
			<div className="min-w-0 flex-1">
				<p className="truncate text-sm font-semibold text-default">
					{t('Get the {{name}} app', { name: t(brandName) })}
				</p>
				<p className="truncate text-xs text-brand-muted">
					{t('Faster ordering on your phone')}
				</p>
			</div>
			<a
				href={phoneStoreUrl}
				target="_blank"
				rel="noopener noreferrer"
				className="flex-none rounded-full bg-portal-accent hover:bg-portal-accent-hover px-4 py-1.5 text-sm font-semibold text-white"
			>
				{t('Get')}
			</a>
			<button
				type="button"
				onClick={() => {
					snoozeAppBar();
					setSnoozed(true);
				}}
				aria-label={t('Close')}
				className="flex-none h-8 w-8 rounded-full text-brand-muted hover:bg-brand-input"
			>
				<FontAwesomeIcon icon={faTimes} />
			</button>
		</div>
	);
}
